import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import usePackages from "../hooks/usePackages";
import { Package } from "../api/models/package.models";
import useProductWithIdentifier from "../hooks/useProductWithIdentifier";
import { Family } from "../api/models/fullProductData.models";
import useModelWithIdentifier from "../hooks/useModelWithIdentifier";
import { Dialog } from "primereact/dialog";
import CreateAkeneoModelForm, {
  AbstractModelWithAmount,
} from "../views/modelForm";
import CreateAkeneoProductForm from "../views/productForm";

const StyledDialog = styled(Dialog)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background-color: #fff;
  padding: 0;

  .p-dialog-content {
    padding: 0 !important;
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }

  & form {
    width: 100%;
  }

  .form-container {
    padding: 0 2rem;
    min-height: calc(100vh - 406px);
    width: 100%;
    max-width: 900px;
    text-align: left;
  }
`;

const GoBackButton = styled.div`
  cursor: pointer;
  color: var(--text-color-secondary);
  margin: 1rem 2rem 0 2rem;
  font-size: 0.9rem;
  text-decoration: underline;
  margin-bottom: 1rem;

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid #007ad9;
    outline-offset: 2px;
  }
`;

interface MaterialPreviewDialogProps {
  onHide: () => void;
  ecomodClasses?: EcomodClass[];
  supClasses?: SupClass[];
  defaultValues?: RowValuesWithEcomodClass[];
  productToPreview?: AbstractModelWithAmount | null;
}

const MaterialPreviewDialog: React.FC<MaterialPreviewDialogProps> = ({
  onHide,
  ecomodClasses,
  supClasses,
  productToPreview,
}) => {
  const { t } = useTranslation();
  const [companyId] = useUIStore(
    useShallow((state: UIState) => [state.companyId]),
  );

  const [previewHistory, setPreviewHistory] = React.useState<
    AbstractModelWithAmount[]
  >(productToPreview ? [productToPreview] : []);
  const [currentIndex, setCurrentIndex] = React.useState(0);

  const [selectedPackage, setSelectedPackage] = React.useState<Package | null>(
    null,
  );

  const [selectMaterialSelection, setSelectMaterialSelection] = React.useState<
    "materials" | "package"
  >("materials");

  const productToPreviewCurrent = previewHistory[currentIndex];

  const { packages, isLoading: isLoadingPackages } = usePackages({
    companyId: companyId as number,
  });

  const { productData, isLoadingProduct } = useProductWithIdentifier({
    identifier:
      !!productToPreviewCurrent &&
      productToPreviewCurrent.family === Family.product
        ? productToPreviewCurrent.identifier
        : undefined,
    companyId: companyId as number,
  });

  const { modelData, isLoadingModel } = useModelWithIdentifier({
    identifier:
      !!productToPreviewCurrent &&
      productToPreviewCurrent.family === Family.product_model
        ? productToPreviewCurrent.identifier
        : undefined,
    companyId: companyId as number,
  });

  const handlePreviewChange = (product: AbstractModelWithAmount) => {
    const previewHistoryCopy = [...previewHistory];
    previewHistoryCopy.push(product);
    setCurrentIndex(previewHistoryCopy.length - 1);
    setPreviewHistory(previewHistoryCopy);
  };

  const handleGoBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setPreviewHistory((prev) => prev.slice(0, -1));
    }
  };

  return (
    <StyledDialog
      visible={true}
      style={{ width: "90vw", maxWidth: "900px", minHeight: "400px" }}
      onHide={() => {
        onHide();
      }}
      closeOnEscape
      header={t("title-product-preview")}
    >
      <LoadingContainer
        status={
          isLoadingPackages || isLoadingProduct || isLoadingModel
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        {currentIndex > 0 && (
          <GoBackButton onClick={handleGoBack} tabIndex={0} role="button">
            {`< ${t("cmd-back-to-previous-preview")}`}
          </GoBackButton>
        )}

        {productData && !isLoadingProduct && (
          <CreateAkeneoProductForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            selectedSearchProduct={undefined}
            onHide={onHide}
            selectMaterialSelection={selectMaterialSelection}
            setSelectMaterialSelection={setSelectMaterialSelection}
            packages={packages || []}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            isLoadingPackages={isLoadingPackages}
            productData={productData}
            preview={true}
          />
        )}
        {modelData && !isLoadingModel && (
          <CreateAkeneoModelForm
            supClasses={supClasses}
            ecomodClasses={ecomodClasses}
            selectedSearchProduct={undefined}
            onHide={onHide}
            modelData={modelData}
            preview={true}
            handlePreviewChange={handlePreviewChange}
            selectMaterialSelection={selectMaterialSelection}
            setSelectMaterialSelection={setSelectMaterialSelection}
            packages={packages || []}
            selectedPackage={selectedPackage}
            setSelectedPackage={setSelectedPackage}
            isLoadingPackages={isLoadingPackages}
          />
        )}
      </LoadingContainer>
    </StyledDialog>
  );
};

export default MaterialPreviewDialog;
